<template>
  <div class="uk-card uk-card-small uk-card-default uk-box-shadow-small uk-margin-bottom">
    <div class="uk-card-body">
      <div class="uk-grid uk-grid-small uk-flex uk-flex-middle">
        <div class="uk-width-auto">
          <button class="uk-button uk-button-primary uk-button-small" @click.prevent="openCreateUserModal(null)">
            <span data-uk-icon="plus-circle"></span>
            Neuen Benutzer anlegen
          </button>
          <modal ref="createUserModal">
            <button class="uk-modal-close-default" type="button" uk-close></button>
            <div class="uk-modal-header">
              <div class="uk-h3">Benutzer</div>
            </div>
            <div class="uk-modal-body" uk-overflow-auto>
              <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
                <div class="uk-grid uk-grid-small" data-uk-grid>
                  <div class="uk-width-1-2@m">
                    <div class="uk-margin">
                      <label class="uk-form-label">Name*</label>
                      <div class="uk-form-controls">
                        <ValidationProvider :rules="'required'" name="name" v-slot="{ errors }">
                          <input
                            class="uk-input"
                            type="text"
                            v-model="form.name"
                            :class="{ 'uk-form-danger': errors[0] }"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                    <div class="uk-margin">
                      <label class="uk-form-label">Passwort (min. 8 Zeichen)*</label>
                      <div class="uk-form-controls">
                        <ValidationProvider :rules="'min:8'" name="password" v-slot="{ errors }">
                          <input
                            class="uk-input"
                            type="password"
                            name="password"
                            v-model="password"
                            :class="{ 'uk-form-danger': errors[0] }"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                  <div class="uk-width-1-2@m">
                    <div class="uk-margin">
                      <label class="uk-form-label">E-Mail*</label>
                      <div class="uk-form-controls">
                        <ValidationProvider :rules="'required|email'" name="email" v-slot="{ errors }">
                          <input
                            class="uk-input"
                            type="text"
                            v-model="form.email"
                            :class="{ 'uk-form-danger': errors[0] }"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                    <div class="uk-margin" v-if="password">
                      <label class="uk-form-label">Passwort wiederholen</label>
                      <div class="uk-form-controls">
                        <ValidationProvider
                          :rules="'required:password|min:8|confirmed:password'"
                          name="password_confirmation"
                          v-slot="{ errors }"
                        >
                          <input
                            class="uk-input"
                            type="password"
                            name="password_confirmation"
                            v-model="passwordConfirmation"
                            :class="{ 'uk-form-danger': errors[0] }"
                          />
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                  <div class="uk-width-1-1" v-if="form.id">
                    <p class="uk-text-small">
                      Geben Sie das Passwort bitte nur ein, wenn das bisherige geändert werden soll. Das neue Passwort
                      muss mindestens 8 Zeichen lang sein.
                    </p>
                  </div>
                  <div class="uk-width-1-2@m">
                    <label class="uk-form-label">Rolle(n)*</label>
                    <div class="uk-margin-small">
                      <ValidationProvider :rules="'required'" name="roles" v-slot="{ errors }">
                        <div class="uk-grid-small uk-child-width-auto uk-grid">
                          <label :class="{ 'uk-form-danger': errors[0] }"
                            ><input class="uk-checkbox" type="checkbox" value="ROLE_MANAGER" v-model="form.roles" />
                            Verkaufsleiter</label
                          >
                          <label :class="{ 'uk-form-danger': errors[0] }"
                            ><input class="uk-checkbox" type="checkbox" value="ROLE_SELLER" v-model="form.roles" />
                            Verkäufer</label
                          >
                        </div>
                      </ValidationProvider>
                    </div>
                  </div>
                </div>
                <div class="uk-grid" data-uk-grid>
                  <div class="uk-width-1-1">
                    <div class="uk-text-right">
                      <button
                        :disabled="sending"
                        class=" uk-button uk-button-primary uk-button-small"
                        @click.prevent="handleSubmit(createUser)"
                      >
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              </ValidationObserver>
            </div>
          </modal>
        </div>
      </div>
      <div class="uk-overflow-auto uk-margin">
        <table class="uk-table uk-table-middle uk-table-hover uk-table-divider">
          <thead>
            <tr>
              <th style="width: 25px;"></th>
              <th class="uk-table-expand">Benutzer</th>
              <th class="uk-table-expand">Login</th>
              <th class="uk-table-expand">Rolle(n)</th>
              <th class="uk-text-center" style="width: 25px;">Erstellt</th>
              <th style="width: 25px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>
                <span data-uk-tooltip="Eingeloggt" v-if="isSelf(user.id)">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    width="20"
                    height="20"
                  >
                    <path
                      d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"
                    />
                  </svg>
                </span>
              </td>
              <td>
                {{ user.name }}
              </td>
              <td>
                {{ user.email }}
              </td>
              <td>
                {{ user.rolesFormatted.join(', ') }}
              </td>
              <td>
                {{ user.createdAt }}
              </td>
              <td class="uk-text-center uk-text-nowrap">
                <div v-if="working(user.id)" data-uk-spinner="ratio: 0.6"></div>
                <a
                  data-uk-tooltip="Status ändern"
                  v-else
                  href="#"
                  @click.prevent="setStatus(user.id)"
                  :style="{
                    color: user.active ? '#228c3a' : '#dc3545',
                    'margin-right': '2px'
                  }"
                >
                  <svg
                    viewBox="0 0 120 120"
                    version="1.1"
                    style="width:20px"
                    fill="currentcolor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="60" cy="60" r="50" />
                  </svg>
                </a>
              </td>
              <td class="uk-text-nowrap">
                <div class="uk-flex uk-flex-right">
                  <div>
                    <a
                      href="#"
                      class="uk-icon-link uk-text-warning"
                      data-uk-icon="pencil"
                      data-uk-tooltip="Benutzer bearbeiten"
                      @click.prevent="openCreateUserModal(user)"
                    ></a>
                  </div>
                  <div v-if="!isSelf(user.id)">
                    <a
                      data-uk-tooltip="Benutzer löschen"
                      href="#"
                      class="uk-icon-link uk-text-danger uk-margin-small-left"
                      data-uk-icon="trash"
                      @click.prevent="remove(user.id)"
                    ></a>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import 'izitoast/dist/css/iziToast.min.css'
import iziToast from 'izitoast/dist/js/iziToast.min.js'
import Modal from '@/components/Modal'
import UserService from '@/services/user.service'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, email, min } from 'vee-validate/dist/rules'

extend('required', {
  ...required,
  message: 'Pflichtfeld'
})

extend('confirmed', {
  ...confirmed
})

extend('email', {
  ...email
})

extend('min', {
  ...min
})

export default {
  props: {
    users: {
      required: true
    }
  },
  components: { Modal, ValidationProvider, ValidationObserver },
  data() {
    return {
      workingIds: [],
      sending: false,
      form: {
        name: '',
        email: '',
        roles: []
      },
      password: '',
      passwordConfirmation: ''
    }
  },
  methods: {
    working(id) {
      return this.workingIds.includes(id)
    },
    openCreateUserModal(user) {
      if (user) {
        this.form = user
      } else {
        this.form = {
          name: '',
          email: '',
          roles: []
        }
      }
      this.$refs.createUserModal.open()
    },
    async createUser() {
      this.sending = true
      const payload = Object.assign({}, this.form, { password: this.password })
      let message
      if (this.form.id) {
        await UserService.update(payload)
        message = 'Benutzer aktualisiert!'
      } else {
        await UserService.create(payload)
        message = 'Benutzer angelegt!'
      }
      this.password = ''
      this.passwordConfirmation = ''
      this.$refs.createUserModal.close()
      this.$emit('created')
      this.sending = false
      iziToast.success({
        title: 'OK',
        message: message,
        timeout: 3000,
        position: 'topCenter'
      })
      this.form = Object.assign(
        {},
        {
          name: '',
          email: '',
          roles: []
        }
      )
    },
    remove(id) {
      const $this = this
      this.$uikit.modal
        .confirm('Benutzer wirklich löschen?', {
          labels: {
            cancel: 'Abbrechen',
            ok: 'Löschen'
          }
        })
        .then(function() {
          UserService.delete(id)
            .then(() => {
              $this.$emit('deleted')
              iziToast.success({
                title: 'OK',
                message: 'Benutzer gelöscht!',
                timeout: 3000,
                position: 'topCenter'
              })
            })
            .catch(() => {
              iziToast.error({
                title: 'OK',
                message: 'Löschen fehlgeschlagen!',
                timeout: 3000,
                position: 'topCenter'
              })
            }),
            function() {
              //
            }
        })
    },
    isSelf(id) {
      return this.$store.state.auth.user.data.id === id
    }
  }
}
</script>
